"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

const rise = {
  hidden: { opacity: 0, y: 24 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  }),
};

export default function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="blueprint-grid pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-6xl gap-12 px-6 py-20 md:grid-cols-[1.15fr_0.85fr] md:py-28">
        {/* Left: copy */}
        <div className="flex flex-col justify-center">
          <motion.p
            custom={0}
            initial="hidden"
            animate="show"
            variants={rise}
            className="mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-accent"
          >
            Workforce management for construction
          </motion.p>
          <motion.h1
            custom={1}
            initial="hidden"
            animate="show"
            variants={rise}
            className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight md:text-7xl"
          >
            The job site,
            <br />
            <span className="text-accent">under control.</span>
          </motion.h1>
          <motion.p
            custom={2}
            initial="hidden"
            animate="show"
            variants={rise}
            className="mt-6 max-w-md text-lg text-muted-foreground"
          >
            Structura Pro keeps your crews, schedules, floor plans, and scopes
            in one place — so the work moves and nothing slips.
          </motion.p>
          <motion.div
            custom={3}
            initial="hidden"
            animate="show"
            variants={rise}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              href="/auth/signup"
              className="group flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Start free trial
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="#demo"
              className="rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              Request a demo
            </a>
          </motion.div>
        </div>

        {/* Right: stylized dashboard mockup */}
        <motion.div
          custom={4}
          initial="hidden"
          animate="show"
          variants={rise}
          className="flex items-center"
        >
          <div className="w-full rounded-md border border-border bg-card p-4 shadow-xl">
            <div className="mb-4 flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-border" />
              <span className="h-2.5 w-2.5 rounded-full bg-border" />
              <span className="h-2.5 w-2.5 rounded-full bg-border" />
            </div>
            <div className="mb-3 h-2 w-1/3 rounded-full bg-accent" />
            <div className="mb-4 grid grid-cols-3 gap-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-sm bg-secondary p-3">
                  <div className="mb-2 h-1.5 w-2/3 rounded-full bg-muted-foreground/40" />
                  <div className="h-4 w-1/2 rounded-sm bg-foreground/80" />
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {[0.9, 0.6, 0.75, 0.4].map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="h-6 w-6 shrink-0 rounded-sm bg-secondary" />
                  <span
                    className="h-3 rounded-full bg-secondary"
                    style={{ width: `${w * 100}%` }}
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
